import * as _ from "lodash";
import * as I from "./CommonTable.d";

const EMPTY_STRING = "";
const PLACE_HOLDER = "-";

const getColumnMode = (config: any) => {
	const freeze = _.get(config, ["column_data", "column", "freeze"], null);
	const group = _.get(config, ["group_header", "group"], null);

	let areGroupsVisible: boolean;

	//return { areGroupsVisible: false, columnMode: I.ColumnMode.flex };
	if (!freeze && group) {
		areGroupsVisible = true;
		return { areGroupsVisible, columnMode: I.ColumnMode.flex };
	} else {
		areGroupsVisible = false;
		return { areGroupsVisible, columnMode: I.ColumnMode.force };
	}
}

const valueProcessing = (
	val: any,
	obj: I.IValueObject,
	tableFloatPrecision: number
): I.TemplateValue => {
	const floatVal = Number(val);

	if (floatVal) {
		const absoluteValue = _.get(obj, ["value", "absoluteValue"], null);
		const floatPrecision = _.get(
			obj,
			["value", "floatPrecision"],
			tableFloatPrecision
		);
		return convertToPreciseAbsolute(
			floatVal,
			absoluteValue,
			floatPrecision
		);
	} else {
		return val;
	}
}

const convertToPreciseAbsolute = (
	value: number,
	isAbsolute: boolean,
	precision?: number
): I.TemplateValue => {
	let res: I.TemplateValue = value;
	if (isAbsolute) {
		res = Math.abs(res);
	}

	if (precision !== undefined && isValid(precision)) {
		res = +res.toFixed(precision);
	}

	return res;
}

const getFlexGrowNumber = (
	config: I.ITableConfig,
	startIndexForFlex: number,
	groupSize: number
): number => {
	const columns = _.get(config, ["column_data", "columns"], []);
	const slicedColumns = columns.slice(
		startIndexForFlex,
		startIndexForFlex + groupSize
	);
	const reducer = (accumulator: number, currentValue: I.IColumn) => {
		const relativeSize = _.get(currentValue, ["width", "relative_size"], 1);
		return accumulator + relativeSize;
	};
	return slicedColumns.reduce(reducer, 0);
}

const preProcessing = (
	config: I.ITableConfig,
	rowsConfig: I.IRows,
	data: any,
	defaultStyles: any
) => {
	const rowDataPath = _.get(rowsConfig, "data_path", null);
	const tableFloatPrecision = _.get(
		config,
		["table", "prop_attributes", "floatPrecision"],
		0
	);

	let rowData;

	if (rowDataPath) {
		rowData = _.get(data, rowDataPath, []);
	} else {
		if (Array.isArray(data)) {
			rowData = data;
		} else {
			return [];
		}
	}

	const columns = _.get(config, ["column_data", "columns"], []);

	const processedRowData = rowData.map((row: any) => {
		const processedRow = {};
		columns.forEach((col, index) => {
			const colIndex = getCustomIndex(col, index);
			const target = processingConst(
				col.cell,
				row,
				defaultStyles,
				tableFloatPrecision
			);
			_.set(processedRow, colIndex, target);
		});
		return processedRow;
	});
	return processedRowData;
}

const addStyleClass = (classList: string[]) => {
	return classList.join(EMPTY_STRING);
}

const processMultiStyleTemplate = (
	obj: any,
	dataObject: any,
	templateParameters: any,
	defaultStyles: any,
	tableFloatPrecision: number
): I.IThresholdStructure => {
	let target: any = {};
	let style: string;
	let thresholdStyles;
	let thresholdDefaultStyles;

	const value = getTemplateValue(obj.value, dataObject);
	_.set(target, "value", value);

	if (templateParameters.hasOwnProperty("threshold")) {
        let thresholdValue: number = 0;
		const threshold = templateParameters.threshold;

		if (threshold.hasOwnProperty("value")) {
			thresholdValue = threshold.value;
		} else if (threshold.hasOwnProperty("prop")) {
			thresholdValue = _.get(dataObject, threshold.prop);
		}

		if (target.value < thresholdValue) {
			thresholdStyles = _.get(
				threshold,
				"lower",
				defaultStyles.lowerThreshold
			);
			thresholdDefaultStyles = defaultStyles.lowerThreshold;
		} else if (target.value > thresholdValue) {
			thresholdStyles = _.get(
				threshold,
				"upper",
				defaultStyles.upperThreshold
			);
			thresholdDefaultStyles = defaultStyles.upperThreshold;
		} else {
			thresholdStyles = _.get(
				threshold,
				"neutral",
				defaultStyles.neutral
			);
			thresholdDefaultStyles = defaultStyles.neutral;
		}
	} else {
		thresholdStyles = templateParameters;
		thresholdDefaultStyles = EMPTY_STRING;
	}

	style = _.get(thresholdStyles, "style", thresholdDefaultStyles);

	const prefixIconClass = _.get(thresholdStyles, "prefixIconClass", null);
	insertValidValue(target, "prefixIconClass", prefixIconClass);

	const suffixIconClass = _.get(thresholdStyles, "suffixIconClass", null);
	insertValidValue(target, "suffixIconClass", suffixIconClass);

	const prefixValue = _.get(
		obj,
		["prefix", "prefixValue"],
		_.get(obj, ["prefix", "prefixProp"])
	);

	if (isValid(prefixValue)) {
		const prefixClass = _.get(
			thresholdStyles,
			"prefixClass",
			thresholdDefaultStyles
		);
		target = {
			...target,
			prefixValue,
			prefixClass,
		};
	}

	const suffixValue = _.get(
		obj,
		["suffix", "suffixValue"],
		_.get(obj, ["prefix", "suffixProp"])
	);

	if (isValid(suffixValue)) {
		const suffixClass = _.get(
			thresholdStyles,
			"suffixClass",
			thresholdDefaultStyles
		);
		target = {
			...target,
			suffixValue,
			suffixClass,
		};
	}

	target = {
		...target,
		style,
		value: valueProcessing(target.value, obj, tableFloatPrecision),
	};

	return target;
}

const processFractionTemplate = (
	obj: any,
	dataObject: any,
	template: any,
	defaultStyles: any,
	tableFloatPrecision: number
): I.IThresholdStructure => {
	let target: any = {};
	let denominator;
	let numerator;

	if (template.hasOwnProperty("values")) {
		const fValues = template.values;
		numerator = fValues.numerator;
		denominator = fValues.denominator;
	} else if (template.hasOwnProperty("props")) {
		const fractionProps = template.props;
		denominator = _.get(dataObject, fractionProps.denominator);
		numerator = _.get(dataObject, fractionProps.numerator);
	}
	const thresholdObj = _.get(
		obj,
		["template", "parameters", "threshold"],
		{}
	);

	const numStyle =
		numerator < denominator
			? _.get(
					thresholdObj,
					["lower", "style"],
					defaultStyles.lowerThreshold
			  )
			: numerator == denominator
			? _.get(thresholdObj, ["neutral", "style"], defaultStyles.neutral)
			: _.get(
					thresholdObj,
					["upper", "style"],
					defaultStyles.upperThreshold
			  );

	const denStyle = _.get(obj, ["template", "parameters", "denominatorStyle"]);

	insertValidValue(target, "denStyle", denStyle);
	insertValidValue(target, "numStyle", numStyle);

	const processedNumerator = valueProcessing(
		numerator,
		template,
		tableFloatPrecision
	);
	const processedDenominator = valueProcessing(
		denominator,
		template,
		tableFloatPrecision
	);

	const isValidFraction = isValid(numerator) && isValid(denominator);

	target = {
		...target,
		isValidFraction,
		numerator: processedNumerator,
		denominator: processedDenominator,
	};

	return target;
}

const processDefaultTemplate = (
	obj: any,
	dataObject: any,
	templateParameters: any,
	defaultStyles: any,
	tableFloatPrecision: number
): I.IThresholdStructure => {
	const target: any = {};
	let style;

	const value = getTemplateValue(obj.value, dataObject);
	_.set(target, "value", value);

	style = _.get(templateParameters, "style", " ");

	if (templateParameters.hasOwnProperty("threshold")) {
		const th = templateParameters.threshold;
		let thresholdValue;

		if (th.hasOwnProperty("value")) {
			thresholdValue = th.value;
		} else if (th.hasOwnProperty("prop")) {
			thresholdValue = _.get(dataObject, th.prop);
		}

		if (target.value <= thresholdValue) {
			const lowerThresholdStyle = _.get(
				th,
				"lowerThresholdStyle",
				defaultStyles.lowerThreshold
			);
			style = addStyleClass([style, lowerThresholdStyle]);
		} else if (target.value > thresholdValue) {
			const upperThresholdStyle = _.get(
				th,
				"upperThresholdStyle",
				defaultStyles.upperThreshold
			);
			style = addStyleClass([style, upperThresholdStyle]);
		} else {
			const neutralStyle = _.get(
				th,
				"neutralStyle",
				defaultStyles.neutral
			);
			style = addStyleClass([style, neutralStyle]);
		}
	}
	const modifiedValue = valueProcessing(
		target.value,
		obj,
		tableFloatPrecision
	);
	_.set(target, "value", modifiedValue);
	_.set(target, "style", style);
	return target;
}

const processingConst = (
	obj: any,
	dataObject: any,
	defaultStyles: any,
	tableFloatPrecision: number
): I.IThresholdStructure | I.TemplateValue => {

	const template = _.get(obj, "template", {});
	const templateType = _.get(template, "type", null);
	const templateParameters = _.get(template, "parameters", {});

	if (templateType === I.TemplateType.Fraction) {
		return processFractionTemplate(
			obj,
			dataObject,
			template,
			defaultStyles,
			tableFloatPrecision
		);
	} else if (templateType === I.TemplateType.MultiStyleTextIcon) {
		return processMultiStyleTemplate(
			obj,
			dataObject,
			templateParameters,
			defaultStyles,
			tableFloatPrecision
		);
	} else {
		if (_.isEmpty(template)) {
			const tValue = getTemplateValue(obj.value, dataObject);
			const modifiedValue = valueProcessing(
				tValue,
				obj,
				tableFloatPrecision
			);
			return modifiedValue;
		} else {
			return processDefaultTemplate(
				obj,
				dataObject,
				templateParameters,
				defaultStyles,
				tableFloatPrecision
			);
		}
	}
}

const createGroupHeaders = (
	config: I.ITableConfig,
	data: any,
	defaultStyles: any
): Array<any> => {
	const isFlex: boolean =
		getColumnMode(config).columnMode === I.ColumnMode.flex;
	const tableFloatPrecision = _.get(
		config,
		["table", "prop_attributes", "floatPrecision"],
		0
	);

	const defaultColumn = {
		resizeable: false,
	};
	let startIndexForFlex = 0;
	const group = _.get(config, ["group_header", "group"], []);

	const processedGroupColumns = group.map((groupObject: any) => {
		let newGroupHeaderColumn = {};
		const name = getTemplateValue(groupObject.header.value, data);
		const headerClass = groupObject.header.style;
		const sticky = groupObject.sticky;

		newGroupHeaderColumn = {
			name,
			headerClass,
			sticky
		};

		if (groupObject.header.hasOwnProperty("template")) {
			const headerData = processingConst(
				groupObject.header,
				data,
				defaultStyles,
				tableFloatPrecision
			) as object;
			_.set(headerData, "value", name);

			const headerTemplateType = getTemplateTypeString(
				groupObject.header
			);
			// const headerTemplate = header;

			newGroupHeaderColumn = {
				...newGroupHeaderColumn,
				headerData,
				headerTemplateType,
				// headerTemplate
			};
		}

		if (isFlex) {
			const flexGrow = getFlexGrowNumber(
				config,
				startIndexForFlex,
				groupObject.group_size
			);
			startIndexForFlex = startIndexForFlex + groupObject.group_size;
			_.set(newGroupHeaderColumn, "flexGrow", flexGrow);
			_.set(newGroupHeaderColumn, 'group_size', groupObject.group_size);
		}
		return { ...defaultColumn, ...newGroupHeaderColumn };
	});
	return processedGroupColumns;
}

const getTemplateValue = (valueObject: any, dataObject: any): string => {
	if (typeof valueObject === "string") {
		return valueObject;
	} else {
		let valuePrefix = EMPTY_STRING;
		let valueSuffix = EMPTY_STRING;
		let result = null;

		if (valueObject.hasOwnProperty("prefixValue")) {
			valuePrefix = valueObject.prefixValue;
		} else if (valueObject.hasOwnProperty("prefixProp")) {
			valuePrefix = _.get(
				dataObject,
				valueObject.prefixProp,
				EMPTY_STRING
			);
		}

		if (valueObject.hasOwnProperty("suffixValue")) {
			valueSuffix = valueObject.suffixValue;
		} else if (valueObject.hasOwnProperty("suffixProp")) {
			valueSuffix = _.get(
				dataObject,
				valueObject.suffixProp,
				EMPTY_STRING
			);
		}

		if (valueObject.hasOwnProperty("value")) {
			result = valueObject.value;
		} else if (valueObject.hasOwnProperty("prop")) {
			result = _.get(dataObject, valueObject.prop, null);
		}

		if (result === null) {
			return PLACE_HOLDER;
		}

		return valuePrefix + result + valueSuffix;
	}
}

const createTableColumns = (
	config: I.ITableConfig,
	rowsConfig: I.IRows,
	data: [{}],
	summaryRowData: I.ISummary,
	defaultStyles: any,
	sortable: boolean
): any => {
	//const columnMode = getColumnMode(config).columnMode;
	const tableFloatPrecision = _.get(
		config,
		["table", "prop_attributes", "floatPrecision"],
		0
	);

	const defaultColumn = {
		resizeable: false,
		sortable,
	};

	const frozenColumn = _.get(config, ["column_data", "column", "freeze"], {});
	const frozenLeftNumber = _.get(frozenColumn, "left", 0);
	const frozenRightNumber = _.get(frozenColumn, "right", 0);
	const columns: Array<any> = _.get(config, ["column_data", "columns"], []);

	const processedColumns = columns.map((col: any, index: number) => {
		let newTableColumn = {};

		const summaryTemplates = _.get(
			rowsConfig,
			["summary", "templates"],
			[]
		);
		const summaryTemplateObject =
			summaryTemplates.length <= index ? {} : summaryTemplates[index];

		const name = getTemplateValue(col.header.value, data);
		const cellClasses = _.get(col.cell, "style", defaultStyles.cell);

		const summaryClass =
			summaryTemplates.length <= index
				? cellClasses
				: _.get(summaryTemplateObject, "style", defaultStyles.summary);

		const cellClass = getCellClass;

		const colIndex = getCustomIndex(col, index);

		const headerClass = _.get(col.header, "style", defaultStyles.header);

		newTableColumn = {
			name,
			prop: colIndex,
			cellClasses,
			summaryClass,
			cellClass,
			headerClass,
		};

		if (col.header.hasOwnProperty("template")) {
			const headerData = processingConst(
				col.header,
				data,
				defaultStyles,
				tableFloatPrecision
			) as object;
			_.set(headerData, "value", name);

			const headerTemplateType = getTemplateTypeString(col.header);
			insertValidValue(
				newTableColumn,
				"headerTemplateType",
				headerTemplateType
			);

			newTableColumn = {
				...newTableColumn,
				headerData,
			};
		}

		if (col.cell.hasOwnProperty("template")) {
			const cellTemplateType = getTemplateTypeString(col.cell);
			_.set(newTableColumn, "cellTemplateType", cellTemplateType);
		}

		const summaryTemplateType = getSummaryTemplateType(index, rowsConfig);
		insertValidValue(
			newTableColumn,
			"summaryTemplateType",
			summaryTemplateType
		);

		const summaryData = getSummaryData(
			summaryTemplates,
			index,
			summaryRowData,
			defaultStyles,
			tableFloatPrecision
		);

		newTableColumn = {
			...newTableColumn,
			summaryData,
		};

		//if (columnMode === I.ColumnMode.flex) {
			const flexGrow = _.get(col, ["width", "relative_size"], 1);
			_.set(newTableColumn, "flexGrow", flexGrow);
		//} else if (columnMode === I.ColumnMode.force) {
			const widthData = _.get(col, ["width", "width_data"], {});
			if (typeof widthData === "number") {
				_.set(newTableColumn, "width", widthData);
			} else {
				if (widthData.hasOwnProperty("minWidth")) {
					_.set(newTableColumn, "minWidth", widthData.minWidth);
				}
				if (widthData.hasOwnProperty("width")) {
					_.set(newTableColumn, "width", widthData.width);
				}
				if (widthData.hasOwnProperty("maxWidth")) {
					_.set(newTableColumn, "maxWidth", widthData.maxWidth);
				}
			}
		//}
		const frozenLeft = index + 1 <= frozenLeftNumber ? true : false;
		const frozenRight =
			_.get(config, ["column_data", "columns"], []).length - index <=
			frozenRightNumber
				? true
				: false;

		newTableColumn = {
			...newTableColumn,
			frozenLeft,
			frozenRight,
		};

		newTableColumn = { ...defaultColumn, ...newTableColumn };
		return newTableColumn;
	});
	return processedColumns;
}

const getSummaryData = (
	summaryTemplates: I.ITemplateConfig[],
	columnIndex: number,
	summaryRowData: any,
	defaultStyles: any,
	tableFloatPrecision: number
) => {
	if (summaryTemplates.length <= columnIndex) {
		return EMPTY_STRING;
	}
	const summaryTemplate = summaryTemplates[columnIndex];

	const template = _.get(summaryTemplate, "template", {});
	const templateType = _.get(template, "type", null);
	const templateParameters = _.get(template, "parameters", {});

	if (templateType) {
		return processingConst(
			summaryTemplate,
			summaryRowData,
			defaultStyles,
			tableFloatPrecision
		);
	} else {
		return processDefaultTemplate(
			summaryTemplate,
			summaryRowData,
			templateParameters,
			defaultStyles,
			tableFloatPrecision
		);
	}
}

const getTemplateTypeString = (obj: I.ITemplateConfig): string => {
	const templateType = _.get(obj, ["template", "type"], null);
	switch (templateType) {
		case I.TemplateType.MultiStyleTextIcon:
			return "multiStyleTextIcon";
		case I.TemplateType.Fraction:
			return "fraction";
		default:
			return "default";
	}
}

const getSummaryTemplateType = (index: number, rowsConfig: I.IRows) => {
	if (_.get(rowsConfig, ["summary", "templates"], []).length <= index) {
		return "default";
	}
	const templateObject = rowsConfig.summary.templates[index];
	return getTemplateTypeString(templateObject);
}

const getCellClass = (obj: any): string => {
    const { row, column, value } = obj;

	if (_.isEmpty(row)) {
		return " " + column.summaryClass;
	} else {
		return " " + column.cellClasses;
	}
}

const getCustomIndex = (col: I.IColumn, index: number) => {
	return [col.header.value, index].join("_");
}

const isValid = (num: I.TemplateValue): boolean => {
	if (num !== null && num !== undefined && num !== PLACE_HOLDER) {
		return true;
	} else {
        return false;
    }
}

const insertValidValue = (target: object, key: string, value: any) => {
	if (isValid(value)) {
		_.set(target, key, value);
	}
}

const getReactTableConfig = (columns: any, columnsGrouping: any) => {
    const transformedColumns = columns.map((c: any, i: number) => {
        let columnObject = {
            cellTemplateType: c.cellTemplateType || 'default',
            prop: c.prop,
            accessor: c.prop,
            cellClasses: c.cellClasses,
            flexGrow: c.flexGrow || 1,
			sticky: (c.frozenLeft) ? 'left' : (c.frozenRight) ? 'right' : null,

            Header: c.headerData || c.name,
            headerClasses: c.headerClass,
            headerTemplateType: c.headerTemplateType,
    
            summaryClass: c.summaryClass,
            summaryTemplateType: c.summaryTemplateType,
            summaryData: c.summaryData,
            //Cell: (cell) => <div>{cell.row.index}</div>
    
            //width: c.width,
            // minWidth: c.minWidth,
            // maxWidth: c.maxWidth
		};
		
		if (c.width) {
			_.set(columnObject, 'width', c.width);
		}
		// if(c.flexGrow) {
		// 	_.set(columnObject, 'width', c.flexGrow);
		// }
    
        return columnObject;
    });

    return (_.isEmpty(columnsGrouping)) ? transformedColumns : getGroupedColumns(columnsGrouping, transformedColumns);
}

const getGroupedColumns = (columnsGrouping: any, columns: any) => {
    let startIndex = 0;

    return columnsGrouping.map((group: any, i: number) => {
        const endIndex = startIndex + group.group_size;
        const arr = columns.slice(startIndex, endIndex);
        startIndex = endIndex; 
        return {
            id: Math.random(),
            isGroupHeader: true,
            Header: group.headerData || group.name,
            headerClasses: group.headerClass,
            headerTemplateType: group.headerTemplateType,
            groupHeaderClass: group.headerClass,
			columns: arr,
			flexGrow: group.flexGrow
            // sticky: (i===0) ? 'left' : null
        }
    })
}

export const commonTableTransformation = ( 
	data: any,
	config: I.ITableConfig,
	componentHeight?: string
) => {
	const headerHeight = 24.5;
	const groupHeaderHeight = 27;
	const summaryPosition = "bottom";

	let defaultStyles = _.get(config, ["column_data", "column", "style"], {
		header: EMPTY_STRING,
		cell: EMPTY_STRING,
	});
	const rowsConfig: I.IRows = config.rows;
	const templateAttributes = _.get(
		config,
		["table", "template_attributes"],
		{}
	);

	const summaryDataPath = _.get(
		rowsConfig,
		["summary", "data_path"],
		EMPTY_STRING
	);
	const summaryRowData = _.get(data, summaryDataPath, {});
	const tableColumnMode = getColumnMode(config).columnMode;

	const summaryStyle = _.get(rowsConfig, ["summary", "style"], EMPTY_STRING);
	const upperThresholdStyle = _.get(
		templateAttributes,
		"upperThreshold",
		EMPTY_STRING
	);
	const lowerThresholdStyle = _.get(
		templateAttributes,
		"lowerThreshold",
		EMPTY_STRING
	);
	const neutralStyle = _.get(
		templateAttributes,
		"neutral",
		upperThresholdStyle
	);

	defaultStyles = {
		...defaultStyles,
		summary: summaryStyle,
		neutral: neutralStyle,
		upperThreshold: upperThresholdStyle,
		lowerThreshold: lowerThresholdStyle,
	};

	let columns;
	let rows;
	let tableProperties;
	let groupHeaderTableProperties: any;
	let columnsGrouping;

	if (!_.isEmpty(config)) {
		if (tableColumnMode === I.ColumnMode.flex) {
			columnsGrouping = createGroupHeaders(config, data, defaultStyles);
		}

		const sortable = config.table.sortable || false;

		columns = createTableColumns(
			config,
			rowsConfig,
			data,
			summaryRowData,
			defaultStyles,
			sortable
		);

		rows = preProcessing(config, rowsConfig, data, defaultStyles);

		const scrollProperties = config.table.scrollProperties;

		const [hasHorizontalScroll, hasVerticalScroll] = scrollProperties
			? [
					scrollProperties.hasHorizontalScroll,
					scrollProperties.hasVerticalScroll,
			  ]
			: [getColumnMode(config).columnMode === I.ColumnMode.force, false];

		tableProperties = {
			columnMode: tableColumnMode,
			headerHeight,
			messages: {
				emptyMessage: '<div class="empty-message">NO DATA</div>',
			},
			summaryPosition,
			scrollbarH: hasHorizontalScroll,
			scrollbarV: hasVerticalScroll,
			summary: _.isEmpty(summaryRowData) ? false : true,
			sortable,
		};

		groupHeaderTableProperties = {
			...groupHeaderTableProperties,
			columnMode: tableColumnMode,
			headerHeight: groupHeaderHeight,
		};
	} else {
		columns = [];
		columnsGrouping = [];
		rows = [];
	}

	const areGroupsVisible = getColumnMode(config).areGroupsVisible;

	const configObject = {
		areGroupsVisible,
		columnsGrouping,
		columns,
		rows,
		tableProperties,
		groupHeaderTableProperties,
		tableColumnMode,
		componentHeight,
	};

	const reactTableConfig = getReactTableConfig(configObject.columns, columnsGrouping);

	return {columns: reactTableConfig, rows, columnMode: getColumnMode(config), height: componentHeight};
}
